// AgenticScan — the second, richer scan surface (ships alongside the original MCP
// Scan queue, which stays up until it's retired). Lists completed scans; opening
// one renders the full tabbed report (McpScanReport) driven by live data from
// GET /scans/{id}. The report degrades gracefully — any pipeline field the console
// doesn't store (lifecycle/cost/engines/callback/tenant) simply isn't shown.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Tag, Button, Alert, Spin, Flex, Input, Segmented, Card, Typography, Checkbox } from "antd";
import type { TableColumnsType } from "antd";
import { ArrowLeft, RefreshCw, Github } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { SeverityText } from "@/components/agate";
import McpScanReport from "@/pages/McpScanReport";
import { useAuth } from "@/lib/auth/AuthProvider";
import { scansApi, scanStatusMeta, severityTone, fmtWhen, type ScanSummary, type ScanResult } from "@/lib/api/scans";
import { fetchScanReport, adaptScanReport, type ScanReportVM } from "@/lib/api/scanReport";

type VisibilityFilter = "" | "public" | "private";

function StatusCell({ s }: { s: ScanSummary }) {
  const m = scanStatusMeta(s);
  return <Tag color={m.tag}>{m.label}</Tag>;
}

export default function AgenticScan() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [rows, setRows] = useState<ScanSummary[]>([]);
  const [listErr, setListErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);
  const [report, setReport] = useState<{ vm: ScanReportVM; raw: unknown } | null>(null);
  const [reportErr, setReportErr] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [visibility, setVisibility] = useState<VisibilityFilter>("");
  const [repoInput, setRepoInput] = useState("");
  const [latestCommit, setLatestCommit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitNote, setSubmitNote] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);

  const loadList = useCallback(async (vis: VisibilityFilter) => {
    setLoading(true);
    setListErr(null);
    try {
      setRows(await scansApi.list(vis));
    } catch (e) {
      setListErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList(visibility);
  }, [loadList, visibility]);

  const openReport = useCallback(async (id: string) => {
    setOpenId(id);
    setReport(null);
    setReportErr(null);
    setReportLoading(true);
    try {
      const raw = await fetchScanReport(id);
      setReport({ vm: adaptScanReport(raw), raw });
    } catch (e) {
      setReportErr(e instanceof Error ? e.message : String(e));
    } finally {
      setReportLoading(false);
    }
  }, []);

  const submitPublic = useCallback(
    async () => {
      const repo = repoInput.trim();
      if (!repo) return;
      setSubmitting(true);
      setSubmitNote(null);
      try {
        // Default = grab the cache (latestCached: true). The "Scan latest commit"
        // checkbox opts into a fresh scan (latestCached: false → resolve HEAD + scan on miss).
        const res = await scansApi.submitPublicScan(repo, { latestCached: !latestCommit });
        if (res.cached) {
          setSubmitNote({
            type: "success",
            text: `Cached result for ${res.repo}${res.ref ? ` @ ${res.ref.slice(0, 7)}` : ""}${
              res.stale_possible ? " (may not be the latest commit)" : ""
            } — opening report.`,
          });
          void openReport(res.scan_id);
        } else {
          setSubmitNote({
            type: "info",
            text: `Scan started for ${res.repo} @ ${res.ref.slice(0, 7)}. It appears below as “Scanning…” and updates when done.`,
          });
          void loadList(visibility);
        }
      } catch (e) {
        setSubmitNote({ type: "error", text: e instanceof Error ? e.message : String(e) });
      } finally {
        setSubmitting(false);
      }
    },
    [repoInput, latestCommit, openReport, loadList, visibility],
  );

  const columns: TableColumnsType<ScanSummary> = useMemo(
    () => [
      { title: "Repository / server", key: "server", render: (_, s) => s.server || s.scan_id },
      {
        title: "Type",
        key: "visibility",
        width: 100,
        render: (_, s) =>
          s.visibility === "public" ? <Tag color="blue">Public</Tag> : <Tag>Private</Tag>,
      },
      {
        title: "Worst severity",
        key: "sev",
        width: 140,
        render: (_, s) => <SeverityText tone={severityTone(s.worst_severity)}>{s.worst_severity || "—"}</SeverityText>,
      },
      {
        title: "Vulnerable",
        key: "vuln",
        width: 110,
        render: (_, s) => <span className="tabular-nums">{s.vulnerable_count}</span>,
      },
      { title: "Status", key: "status", width: 130, render: (_, s) => <StatusCell s={s} /> },
      { title: "Scanned", key: "when", width: 180, render: (_, s) => fmtWhen(s.created_at) },
    ],
    [],
  );

  // ── report view ──
  if (openId) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          type="text"
          icon={<ArrowLeft size={15} />}
          onClick={() => {
            setOpenId(null);
            setReport(null);
            setReportErr(null);
          }}
          style={{ alignSelf: "flex-start", paddingLeft: 0 }}
        >
          All scans
        </Button>
        {reportErr && <Alert type="error" showIcon message={reportErr} />}
        {reportLoading && !report && (
          <Flex align="center" gap={8} style={{ padding: "32px 0" }}>
            <Spin /> <span>Loading report…</span>
          </Flex>
        )}
        {report && (
          <McpScanReport
            vm={report.vm}
            raw={report.raw}
            scanResult={report.raw as ScanResult}
            isAdmin={isAdmin}
            onApproved={() => openId && openReport(openId)}
          />
        )}
      </div>
    );
  }

  // ── list view ──
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Agentic Scan"
        subtitle="Completed agentic scans across your repositories. Open one to review its MCP servers, skills, and agentic code before you allow them, with a trust score, worst severity, and findings."
        actions={
          <Button icon={<RefreshCw size={14} />} onClick={() => loadList(visibility)}>
            Refresh
          </Button>
        }
      />
      <Alert
        type="info"
        showIcon
        message="These scans are advisory. They inform your review and do not enforce or block anything at runtime."
      />

      {isAdmin && (
        <Card size="small" title={<Flex align="center" gap={8}><Github size={16} /> Scan a public repository</Flex>}>
          <Flex gap={12} wrap align="center">
            <Input
              placeholder="https://github.com/owner/repo"
              value={repoInput}
              onChange={(e) => setRepoInput(e.target.value)}
              onPressEnter={() => void submitPublic()}
              style={{ maxWidth: 480 }}
              disabled={submitting}
              allowClear
            />
            <Button type="primary" loading={submitting} disabled={!repoInput.trim()} onClick={() => void submitPublic()}>
              Scan repository
            </Button>
            <Checkbox checked={latestCommit} onChange={(e) => setLatestCommit(e.target.checked)} disabled={submitting}>
              Scan latest commit
            </Checkbox>
          </Flex>
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
            Looks the repository up in the MCP Trust registry and shows its existing scan — instant. Tick
            “Scan latest commit” to fetch the current commit and run a fresh scan (takes longer). Public repo
            scans are shared with your team and are advisory only.
          </Typography.Paragraph>
          {submitNote && (
            <Alert
              style={{ marginTop: 12 }}
              type={submitNote.type}
              showIcon
              closable
              onClose={() => setSubmitNote(null)}
              message={submitNote.text}
            />
          )}
        </Card>
      )}

      {listErr && <Alert type="error" showIcon message={listErr} />}
      <Flex justify="flex-start">
        <Segmented
          value={visibility || "all"}
          onChange={(v) => setVisibility(v === "all" ? "" : (v as VisibilityFilter))}
          options={[
            { label: "All", value: "all" },
            { label: "Public", value: "public" },
            { label: "Private", value: "private" },
          ]}
        />
      </Flex>
      <Table<ScanSummary>
        size="middle"
        rowKey={(s) => s.scan_id}
        loading={loading}
        dataSource={rows}
        columns={columns}
        onRow={(s) => ({ onClick: () => openReport(s.scan_id), style: { cursor: "pointer" } })}
        locale={{ emptyText: "No scans yet." }}
        pagination={rows.length > 20 ? { pageSize: 20, size: "small" } : false}
      />
    </div>
  );
}
